/**
 * Derived case statistics + handover-seam detection over the fusion seed.
 * Pure functions — no store, no Cytoscape. Any UI (case summary chip, evidence
 * ledger, a future timeline) reads from here. Mirrors how a production build
 * would derive these from the merged envelope set. See MULTIMODAL_ENVELOPE_CONTRACT.md.
 */
import { SEED_NODES, SEED_EDGES } from './fusionSeed';
import type { FusionModule, FusionEdge, FusionNode } from '../types/fusion';

export interface HandoverSeam {
  edgeId: string;
  from: FusionModule;
  to: FusionModule;
  edge_type: string;
  determinism: 'deterministic' | 'scored';
  join_key?: string;
  label: string;
}

export interface FusionStats {
  surfaces: FusionModule[];
  nodeCount: number;
  edgeCount: number;
  deterministicEdges: number;
  scoredEdges: number;
  valueByUnit: Record<string, number>;
  handovers: HandoverSeam[];
}

const moduleOf = (id: string, nodes: FusionNode[]): FusionModule | undefined =>
  nodes.find((n) => n.id === id)?.module;

/** A handover seam = an edge whose endpoints live in different modules (a cross-surface join). */
export function detectHandovers(nodes = SEED_NODES, edges = SEED_EDGES): HandoverSeam[] {
  const seams: HandoverSeam[] = [];
  for (const e of edges) {
    const from = moduleOf(e.source, nodes);
    const to = moduleOf(e.target, nodes);
    if (!from || !to || from === to) continue;
    // The identity hub ('shared') consolidates everything — those are joins, not surface handovers.
    seams.push({
      edgeId: e.id,
      from,
      to,
      edge_type: e.edge_type,
      determinism: e.determinism,
      join_key: e.join_key,
      label: `${from} → ${to}`,
    });
  }
  return seams;
}

export function computeStats(step?: number, nodes = SEED_NODES, edges = SEED_EDGES): FusionStats {
  const inStep = (n: { step?: number }) => step == null || (n.step ?? 1) <= step;
  const ns = nodes.filter(inStep);
  const nids = new Set(ns.map((n) => n.id));
  const es = edges.filter((e) => inStep(e) && nids.has(e.source) && nids.has(e.target));

  const valueByUnit: Record<string, number> = {};
  for (const e of es) {
    if (e.value && e.unit) valueByUnit[e.unit] = (valueByUnit[e.unit] || 0) + e.value;
  }

  return {
    surfaces: [...new Set(ns.map((n) => n.module))],
    nodeCount: ns.length,
    edgeCount: es.length,
    deterministicEdges: es.filter((e) => e.determinism === 'deterministic').length,
    scoredEdges: es.filter((e) => e.determinism === 'scored').length,
    valueByUnit,
    handovers: detectHandovers(ns, es),
  };
}

/** The deterministic, court-defensible chain only — sorted by evidence tier (0 strongest). */
export function deterministicChain(edges = SEED_EDGES): FusionEdge[] {
  return edges
    .filter((e) => e.determinism === 'deterministic')
    .slice()
    .sort((a, b) => a.attribution.tier - b.attribution.tier);
}
